import { captureException, withScope } from '@sentry/nextjs';

interface IStringSplitOptions {
  keys: string;
  values: string;
}

const isNullOrEmpty = (value: string) =>
  typeof value === 'undefined' || value === null || value === '';

// Not sure where the default date came into play but read note on 'compare' about how
// it's maybe that not intuitive
const isNullOrEmptyOrDefault = (value: string) =>
  isNullOrEmpty(value) || value === '1900-01-01T00:00:00';

/**
 * Converts a serialized string into an object of key/value pairs.
 * Ex. abc=1&def=2 - {abc:1, def:2}
 * Note: Does not handle multiple values with same key
 * @param {*} serializedString
 * @param {*} splitOptions By default keys:&, values:=
 */
export const deserializeKeyValuePairs = (
  serializedString: string = '',
  splitOptions: IStringSplitOptions = { keys: '&', values: '=' }
): [string, string] =>
  serializedString.split(splitOptions.keys).reduce((acc, cur) => {
    const [property, value] = cur.split(splitOptions.values);
    acc[property] = value;
    return acc;
  }, {}) as [string, string];

// https://stackoverflow.com/questions/23136691/replace-last-occurrence-word-in-javascript/23137090
export const replaceLast = (
  source: string,
  replace: string,
  withNewText: string
): string => {
  // find the index of last time word was used
  // please note lastIndexOf() is case sensitive
  const n = source.lastIndexOf(replace);

  // slice the string in 2, one from the start to the lastIndexOf
  // and then replace the word in the rest
  return source.slice(0, n) + source.slice(n).replace(replace, withNewText);
};

const DefaultEqualsCollator: Intl.CollatorOptions = {
  sensitivity: 'accent'
};

export const equals = (
  a: string,
  b: string,
  options: Intl.CollatorOptions = DefaultEqualsCollator
): boolean => {
  if (typeof a !== 'string' || typeof b !== 'string') {
    return false;
  }

  return a.localeCompare(b, undefined, options) === 0;
};

export interface IStringCompareOptions {
  isDescending?: boolean;
  isShowEmptyFirst?: boolean;
  collator?: Intl.Collator;
}

/**
 * Takes string numbers into account and orders numeric first, case insensitive.
 */
const DefaultStringCompareOptions: IStringCompareOptions = {
  isDescending: false,
  isShowEmptyFirst: false,
  collator: new Intl.Collator('en', {
    numeric: true,
    sensitivity: 'base'
  })
};

/**
 * String compare using preferred js method.
 * Note: This a baked in logic to short default dates as a string (1900-01-01T00:00:00) to the top.
 * - I'm actually not sure why that's helpful because other dates will do an alpha-numeric which may not give the expected results
 * @param a
 * @param b
 * @param isDescending
 * @param isShowEmptyFirst
 * @returns
 */
export const compare: (
  a: string,
  b: string,
  options?: IStringCompareOptions
) => number = (a, b, options = DefaultStringCompareOptions) => {
  if (a === b) {
    return 0;
  }

  const emptyA = isNullOrEmptyOrDefault(a);
  const emptyB = isNullOrEmptyOrDefault(b);

  // Send nulls, empty strings, and default dates to the bottom of a list.
  if (emptyA && emptyB) return 0;
  else if (emptyA) return options.isShowEmptyFirst ? -1 : 1;
  else if (emptyB) return options.isShowEmptyFirst ? 1 : -1;

  let ret = (
    options.collator || (DefaultStringCompareOptions.collator as Intl.Collator)
  ).compare(a, b);

  if (options.isDescending) {
    ret = -ret;
  }

  return ret;
};

export const pluralize = (str: string): string => {
  if (!str) return '';

  if (
    str.endsWith('ch') ||
    str.endsWith('s') ||
    str.endsWith('sh') ||
    str.endsWith('x') ||
    str.endsWith('z')
  ) {
    return `${str}es`;
  }

  return `${str}s`;
};

export const depluralize = (str: string): string => {
  if (!str || !str.length) return str;

  if (str.charAt(str.length - 1) === 's') {
    // e.g. Classes
    if (
      str.length > 2 &&
      str.charAt(str.length - 2) === 'e' &&
      str.charAt(str.length - 3) === 's'
    ) {
      return str.slice(0, str.length - 2);
    }
    // e.g. Cats, Conferences
    return str.slice(0, str.length - 1);
  }

  return str;
};

const VOWELS = ['a', 'e', 'i', 'o', 'u'];

export const getSingularArticle = (str: string): string => {
  if (!str || !str.length) return 'a';

  const firstChar = str.charAt(0);

  if (VOWELS.includes(firstChar.toLowerCase())) return 'an';

  return 'a';
};

type TJsonParseTuple = [Error | null, any, string | null];

/**
 * Prevents exceptions from being throw whlie deserializing json.
 * Returns a tuple of [error, data, raw].
 * While 'null' is not preferred,
 * the current use case for this is it gets parsed and serialized immediately and undefined can't be serialized.
 * @param str
 * @returns
 */
export const safeJsonParse: (str: string) => TJsonParseTuple = str => {
  if (typeof str !== 'string') {
    return [
      new Error("Can't parse non-string values to JSON"),
      null,
      str
    ] as TJsonParseTuple;
  }

  try {
    return [null, JSON.parse(str), str];
  } catch (error) {
    if (error instanceof Error) {
      return [error, null, str];
    }

    return [new Error('Unknown error occurred'), null, str];
  }
};

export const sentryLogSafeJsonParse: (
  data: TJsonParseTuple,
  transactionName: string
) => TJsonParseTuple = (data, transactionName) => {
  const [error, , raw] = data;

  if (error) {
    withScope(scope => {
      scope.setTransactionName(transactionName);
      scope.setContext('raw', {
        data: raw
      });
      captureException(error);
    });
  }

  return data;
};

export const upperCaseFirstChar = value =>
  (value || '').charAt(0).toUpperCase() + value.slice(1);

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i;

export const getIsValidEmail = (email: string): boolean =>
  !!email && EMAIL_REGEX.test(email);

const PHONE_REGEX = /\d{10}/g; // Limiting to 10 numeric digits, but could be expanded. The DB column is a varchar(20).

export const getIsValidPhone = (phone: string): boolean =>
  !!phone && phone.length === 10 && PHONE_REGEX.test(phone);

/**
 * Converts strings live CONTENT_START to onContentStart
 * Important note: This prefixes the on too
 * @param eventName
 * @returns
 */
export const convertToEventCase = (eventName: string): string => {
  if (!eventName) return eventName;

  const modified: string[] = [];
  // adding one so that i don't have to do extra logic in the for loop below
  const indicesOfUnderscore: number[] = [-1];

  for (let i = 0; i < eventName.length; i++) {
    const ch = eventName.charAt(i);

    if (ch === '_' || ch === ' ') {
      indicesOfUnderscore.push(i);
      continue;
    }

    modified.push(
      i === 0 || indicesOfUnderscore[indicesOfUnderscore.length - 1] === i - 1
        ? ch.toUpperCase()
        : ch.toLowerCase()
    );
  }

  return 'on' + modified.join('');
};
